/** Inline tool call — TaskThread-style row with success/failure and toolbox tooltip. */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import type { AgentRun } from "./types";
import { toolDisplay } from "./phases";
import { toolOutcomeFromOutput, type ToolOutcome } from "./toolStatus";

interface ToolUsedLineProps {
  run: AgentRun;
  toolName: string;
  output?: Record<string, unknown>;
}

function outcomeMeta(outcome: ToolOutcome): { icon: typeof faCircleCheck; tone: string; label: string } {
  if (outcome === "success") {
    return { icon: faCircleCheck, tone: "text-emerald-400", label: "Succeeded" };
  }
  if (outcome === "error") {
    return { icon: faCircleXmark, tone: "text-rose-400", label: "Failed" };
  }
  return { icon: faCircleCheck, tone: "text-slate-500", label: "Completed" };
}

export function ToolUsedLine({ run, toolName, output }: ToolUsedLineProps) {
  const tool = toolDisplay(run, toolName);
  const inToolbox = run.available_tools.some((t) => t.name === toolName);
  const outcome = toolOutcomeFromOutput(output);
  const meta = outcomeMeta(outcome);

  const label = (
    <span
      className={
        inToolbox
          ? "text-slate-300 underline decoration-dotted decoration-slate-600 underline-offset-2 cursor-help"
          : "text-amber-300/90"
      }
    >
      {tool.friendly}
    </span>
  );

  return (
    <div className="space-y-1 pl-1">
      <div className="flex items-center gap-2 text-xs text-slate-400 py-0.5">
        <FontAwesomeIcon icon={meta.icon} className={`text-[10px] shrink-0 ${meta.tone}`} />
        <span className="min-w-0">
          {meta.label}: used{" "}
          {inToolbox ? (
            <Tooltip
              placement="top"
              maxWidth="min(20rem, 92vw)"
              content={
                <div className="text-left space-y-1">
                  <p className="text-sm font-medium text-slate-100">{tool.friendly}</p>
                  {tool.description ? (
                    <p className="text-xs text-slate-300 leading-relaxed">{tool.description}</p>
                  ) : null}
                  <p className="text-[10px] text-emerald-400/80">In toolbox</p>
                </div>
              }
            >
              {label}
            </Tooltip>
          ) : (
            <>
              {label}
              <span className="text-slate-500"> (not in toolbox)</span>
            </>
          )}
        </span>
      </div>
      {output && Object.keys(output).length > 0 ? <DebugOutput output={output} outcome={outcome} /> : null}
    </div>
  );
}

function DebugOutput({ output, outcome }: { output: Record<string, unknown>; outcome: ToolOutcome }) {
  return (
    <details className="ml-5 text-xs text-slate-500">
      <summary className="cursor-pointer hover:text-slate-400 select-none">
        What the agent saw (debug)
        {outcome === "error" && typeof output.error === "string" ? ` — ${output.error}` : ""}
      </summary>
      <pre className="mt-2 max-h-48 overflow-x-auto rounded-lg bg-slate-900/80 p-2 text-[11px] text-slate-300 ring-1 ring-white/5">
        {JSON.stringify(output, null, 2)}
      </pre>
    </details>
  );
}
